import Joi from 'joi';

import { type OnboardingFormValues, type OnboardingMode } from '@/domain/onboarding/types';
import { PARTNER_CATEGORIES } from '@/domain/onboarding/utils';
import { BRAZIL_STATES } from '@/utils/data';
import { translatedMessages } from '@/i18n';
import { isValidTaxId } from '@/utils/document';

const NS = 'domain.onboarding.schemas';

const optionalText = (max: number) =>
  Joi.string()
    .trim()
    .max(max)
    .allow('')
    .error(translatedMessages(NS, { 'string.max': 'tooLong' }));

/**
 * Mirrors the API validation (`partners.validations.ts`) so most mistakes are caught before
 * submit. The terms checkbox is required only when creating the account.
 */
export const buildOnboardingSchema = (mode: OnboardingMode) =>
  Joi.object<OnboardingFormValues>({
    displayName: Joi.string()
      .trim()
      .min(2)
      .max(80)
      .required()
      .error(
        translatedMessages(NS, {
          'string.empty': 'displayNameRequired',
          'any.required': 'displayNameRequired',
          'string.min': 'displayNameShort',
          'string.max': 'tooLong',
        }),
      ),
    legalName: Joi.string()
      .trim()
      .min(2)
      .max(120)
      .required()
      .error(
        translatedMessages(NS, {
          'string.empty': 'legalNameRequired',
          'any.required': 'legalNameRequired',
          'string.min': 'legalNameShort',
          'string.max': 'tooLong',
        }),
      ),
    taxId: Joi.string()
      .required()
      .custom((value: string, helpers) =>
        isValidTaxId(value) ? value : helpers.error('any.invalid'),
      )
      .error(
        translatedMessages(NS, {
          'string.empty': 'taxIdRequired',
          'any.required': 'taxIdRequired',
          'any.invalid': 'taxIdInvalid',
        }),
      ),
    category: Joi.string()
      .valid(...PARTNER_CATEGORIES)
      .required()
      .error(
        translatedMessages(NS, {
          'any.only': 'categoryRequired',
          'string.empty': 'categoryRequired',
          'any.required': 'categoryRequired',
        }),
      ),
    description: optionalText(280),
    logoUrl: Joi.string().allow(''),
    contactName: Joi.string()
      .trim()
      .min(2)
      .max(120)
      .required()
      .error(
        translatedMessages(NS, {
          'string.empty': 'contactNameRequired',
          'any.required': 'contactNameRequired',
          'string.min': 'contactNameRequired',
          'string.max': 'tooLong',
        }),
      ),
    contactEmail: Joi.string()
      .trim()
      .email({ tlds: { allow: false } })
      .required()
      .error(
        translatedMessages(NS, {
          'string.empty': 'contactEmailRequired',
          'any.required': 'contactEmailRequired',
          'string.email': 'contactEmailInvalid',
        }),
      ),
    contactPhone: Joi.string()
      .pattern(/^\D*(\d\D*){10,11}$/)
      .allow('')
      .error(translatedMessages(NS, { 'string.pattern.base': 'phoneInvalid' })),
    city: optionalText(80),
    state: Joi.string()
      .valid(...BRAZIL_STATES, '')
      .error(translatedMessages(NS, { 'any.only': 'stateInvalid' })),
    website: Joi.string()
      .trim()
      .uri({ scheme: ['http', 'https'] })
      .allow('')
      .error(translatedMessages(NS, { 'string.uri': 'websiteInvalid' })),
    instagram: Joi.string()
      .trim()
      .pattern(/^@?[A-Za-z0-9._]{1,30}$/)
      .allow('')
      .error(translatedMessages(NS, { 'string.pattern.base': 'instagramInvalid' })),
    acceptTerms:
      mode === 'create'
        ? Joi.boolean()
            .valid(true)
            .required()
            .error(
              translatedMessages(NS, {
                'any.only': 'termsRequired',
                'any.required': 'termsRequired',
              }),
            )
        : Joi.boolean(),
  });
